/**
 * Agent CRM layout with sidebar navigation and top bar.
 *
 * Provides the standard layout for all agent CRM pages:
 * - Navy sidebar with CRM navigation links
 * - Top bar with search, notifications, and the user avatar-pill menu (#2579)
 * - Responsive: sidebar collapses on mobile with hamburger menu
 * - Desktop icon-rail collapse (#2579): the content offset animates in lockstep
 *   with the sidebar (`lg:ml-64` ↔ `lg:ml-16`).
 * - Data protection measures for agent role (D2-SEC-002, D2-SEC-003)
 * - Watermark overlay for agent role (D2-SEC-003)
 */
import { useState } from "react";
import { Outlet } from "react-router-dom";
import { Menu } from "lucide-react";
import { useTour } from "../../contexts/tour";
import { useIsViewingAs } from "../../contexts/viewAs";
import { CrmSidebar } from "./CrmSidebar";
import { UserMenu } from "./UserMenu";
import { PageContainer } from "./PageContainer";
import { PreviewBanner } from "./PreviewBanner";
import { ViewAsBanner } from "./ViewAsBanner";
import { FeedbackButton } from "../feedback/FeedbackButton";
import { ViewSwitcher } from "./ViewSwitcher";
import { GlobalSearchProvider } from "../search/GlobalSearchProvider";
import { HeaderSearchButton } from "../search/HeaderSearchButton";
import { NotificationBell } from "../notifications/NotificationBell";
import { TourLauncher } from "../tours/TourLauncher";
import { SupportBubble } from "../support/SupportBubble";
import { Watermark } from "../crm/Watermark";
import { useDataProtection } from "../../hooks/useDataProtection";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";

export function CrmLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // A running guided tour can force the mobile drawer open so it can highlight
  // a sidebar item below the lg: breakpoint (#1321).
  const { mobileDrawerOpen } = useTour();
  // #2579: content offset tracks the desktop icon-rail collapse preference.
  const { collapsed } = useSidebarCollapsed();
  const viewingAs = useIsViewingAs();

  // D2-SEC-002, D2-SEC-003: Apply data protection for agent role
  useDataProtection();

  // #1492: the persona-preview banner is a top-mounted, full-width toolbar in
  // normal flow at the top of the main column -- it pushes the header + content
  // DOWN beneath it (never overlaps), replacing the old floating pill.
  //
  // #1739: the header is the FIRST CHILD of the scrolling <main> (not a flex
  // sibling above it), so page content scrolls UNDERNEATH the sticky header --
  // which is what makes the #1718 frosted-glass (backdrop-blur) actually act on
  // real content instead of reading as a flat solid bar. The scroll container
  // is now <main> itself; the header keeps its own px/py and the content gets
  // the p-4 sm:p-6 gutter via the inner wrapper.
  return (
    <GlobalSearchProvider>
    <div className="h-screen overflow-hidden flex bg-[var(--color-bg-page)]">
      {/* Sidebar */}
      <CrmSidebar isOpen={sidebarOpen || mobileDrawerOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content area — offset animates with the rail collapse (#2579). */}
      <div
        className={`flex-1 flex flex-col min-w-0 transition-[margin] duration-200 ease-in-out motion-reduce:transition-none ${
          collapsed ? "lg:ml-16" : "lg:ml-64"
        }`}
      >
        {/* #1492: top-mounted, in-flow persona-preview toolbar (admin only,
            while previewing). Sits above the scroll region and pushes it down
            -- never floats/overlaps. Renders null when not previewing. */}
        <PreviewBanner />
        {/* D2-SEC-011 (#3944): read-only "Viewing as <agent>" toolbar. */}
        <ViewAsBanner />

        {/* The scroll region. The sticky header is its FIRST child (#1739) so
            content slides up BEHIND the glass header. #3865: `relative` makes
            <main> the containing block for every absolutely positioned
            descendant (e.g. `sr-only` file inputs), so its overflow clips them.
            Without it they escape to the document, the document itself
            becomes scrollable, and the browser focusing a hidden input (as it
            does when a file chooser closes) scrolls the window and slides the
            whole shell out of view. Guarded by NoUnpositionedScrollingMain3865. */}
        <main className="relative flex-1 overflow-y-auto">
          {/* Top bar (#1677): evenly-spaced header mirroring the admin shell
              (#1655). The left cluster (hamburger + workspace switcher) and the
              right action cluster each use a single consistent gap; all
              interactive items meet the >=44px touch target. On small screens the
              verbose user label collapses so the icon row never crowds or wraps.
              Shared header, so the fix improves every /crm/* dashboard. #1739:
              sticky INSIDE the scroll container so the #1718 backdrop-blur
              reveals the content scrolling beneath it. */}
          <header className="sticky top-0 z-30 bg-[var(--color-bg-card)]/80 supports-[backdrop-filter]:bg-[var(--color-bg-card)]/70 backdrop-blur-md backdrop-saturate-150 border-b border-[var(--color-border)] px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2" data-tour-sticky-header>
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden flex items-center justify-center min-h-[44px] min-w-[44px] -ml-1 rounded-lg hover:bg-[var(--color-bg-page)] transition-colors"
                aria-label="Open sidebar"
              >
                <Menu className="h-5 w-5 text-[var(--color-text-primary)]" />
              </button>

              <ViewSwitcher />
            </div>

            <div className="flex items-center gap-1 sm:gap-2" data-tour="header-toolbar">
              <TourLauncher />
              <HeaderSearchButton />
              <NotificationBell />
              {/* D2-SEC-013 (#3999): feedback stays available while viewing
                  as an agent -- the one write besides Exit the server permits
                  in view-as, filed under the viewer, not the agent. */}
              <FeedbackButton />
              {/* #2579: avatar-pill account menu (absorbs the standalone logout
                  button + surfaces theme + settings). */}
              <UserMenu />
            </div>
          </header>

          {/* Page content -- keeps the p-4 sm:p-6 gutter, now scrolling behind
              the sticky header above. */}
          <div className="p-4 sm:p-6">
            <PageContainer>
              <Outlet />
            </PageContainer>
          </div>
        </main>
      </div>

      {/* D10-UI-001: help assistant bubble -- signed-in staff shells only;
          renders nothing while the service is disabled, or while viewing as
          an agent (every question is a write the server refuses). */}
      {!viewingAs && <SupportBubble />}

      {/* D2-SEC-003: Watermark overlay for agent role */}
      <Watermark />
    </div>
    </GlobalSearchProvider>
  );
}
